import React, { useRef, useState, useEffect } from "react";

import classNames from "classnames";
import Link from "next/link";

import { ProductData, UserProductPurchaseData, ContentData } from "data/product/products";
import { useWindowSize } from "usehooks-ts";

import { ImageWithBlur } from "~/components/common/ImageWithBlur/ImageWithBlur";
import { isScrollAvailable } from "~/util/sizeUtils";
import { getProductSlug, convertSecondsToVideoPlayerDuration } from "~/util/stringUtils";

import * as styles from "./Playlist.css";
import { UpdatingVideoProgressBar } from "./UpdatingVideoProgressBar";

export const Playlist = ({
  productData,
  currentVideo,
  currentVideoProgress,
  showOverlay,
  setCurrentVideo,
}: {
  productData: ProductData;
  currentVideo: ContentData;
  currentVideoProgress?: number;
  showOverlay: boolean;
  setCurrentVideo: (video: ContentData) => void;
}): React.ReactElement => {
  const screen = useWindowSize();
  const playListRef = useRef<any>(null);
  const [scrollAvailable, setScrollAvailable] = useState(false);

  useEffect(() => {
    setScrollAvailable(isScrollAvailable(playListRef.current));
    const index = productData.content.findIndex((el) => el.id === currentVideo.id);
    if (index === 0) return;
    const offsetTop = playListRef.current.children[index].offsetTop;
    playListRef.current.scrollTop = offsetTop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen.width, screen.height]);

  const handleClick = (video: ContentData, index: number) => {
    setCurrentVideo(video);
    if (index === 0) {
      playListRef.current.scrollTop = 0;
      return;
    }
    const offsetTop = playListRef.current.children[index].offsetTop;
    playListRef.current.scrollTop = offsetTop;
  };

  const progressForCurrentVideo = (playlistVideoId: string) => {
    if (currentVideo.id === playlistVideoId) {
      return currentVideoProgress;
    }
    return;
  };

  return (
    <>
      <div
        ref={playListRef}
        onScroll={() => {
          setScrollAvailable(isScrollAvailable(playListRef.current));
        }}
        className={styles.playlistBox}
      >
        {productData.content.map((video, index) => (
          <div
            key={video.id}
            className={classNames(
              video.id === currentVideo.id
                ? styles.playlistBoxWrapSelected
                : styles.noneSelectedPlaylistBoxWrap,
              styles.playlistBoxWrap,
            )}
            onClick={() => handleClick(video, index)}
          >
            <div className={styles.playlistBoxImgWrap}>
              <div className={styles.playlistImgWrap}>
                <ImageWithBlur
                  src={video.previewImageUrl ? video.previewImageUrl : productData.productImageURL}
                  width={137}
                  height={76}
                  layout={"fixed"}
                  objectFit={"cover"}
                  alt={video.title}
                  style={{ borderRadius: "3px" }}
                />
              </div>
              <span className={styles.playlistDuration}>
                {convertSecondsToVideoPlayerDuration(video.lengthInSeconds)}
              </span>
              <UpdatingVideoProgressBar
                productId={productData.id}
                currentVideo={currentVideo.id}
                playlistVideoId={video.id}
                currentProgress={progressForCurrentVideo(video.id)}
              />
            </div>
            <h4
              className={classNames(
                styles.playlistBoxVideoTitle,
                video.id === currentVideo.id && styles.playlistBoxVideoSelected,
              )}
            >
              {index + 1}. {video.title}
            </h4>
          </div>
        ))}
      </div>
      {showOverlay && scrollAvailable && <div className={styles.playlistBoxOverlay}></div>}
    </>
  );
};
